import { IOmniaShell } from "@omnia/types"
import { IDashboardService } from "../types"
import { Card } from "@omnia/shared/react/Card"
import { useAsyncValue } from "@omnia/shared/react/hooks"
import { NotificationGroup } from "./types";
import { NotificationGroupComponent } from "./NotificationGroupComponent";
import { useCallback, useEffect, useState } from "react";
import { getCroppingInfo } from "./cropping-info";
import { checkPlansToRecalculate } from "./plan-recalc";
import { checkFarmNLoading, checkNMaxCompliance, checkRequirementsToRecalc } from "./nutrition";
import { checkManureAreas } from "./manure-area";
import { checkBoundaryAreas } from "./boundary-area";
import { checkPSorpCapacity } from "./psorpCapacity";
import { formatRemoteNotifications } from "./remote";

interface Props {
    svc: IDashboardService
    shell: IOmniaShell
    update(run?: () => void): void
}

export function WarningsCard({svc, shell, update}: Props) {
    const [notifications, setNotifications] = useState<NotificationGroup[]>([]);

    /** Loads additional notifications, merge with the baseGroups, and set as the new state of the component for rendering */
    const refresh = useCallback((localGroups: NotificationGroup[]) => {
        if (localGroups) {
            const ntf = svc.getNotifications() ?? [];
            const groups = expandGroupsWithErrors(formatRemoteNotifications(ntf, svc));
            const result = localGroups.concat(groups);
            setNotifications(result);
        }
    }, [svc, svc.yearId, svc.stateVersion]);

    // on every service/year change, get the local notifications, and also set the notifications array
    const local: NotificationGroup[] = useAsyncValue(() => svc.ensureInit().then(() => {
        const cropping = getCroppingInfo(svc)
        const nutrition = svc.getNutritionInfo()
        const holding = svc.holding

        const ntf = [
            checkPlansToRecalculate(svc.getNutritionPlans(), svc.getSeedPlans()),
            checkRequirementsToRecalc(nutrition.RequirementsToRecalculate, cropping),
            checkManureAreas(cropping, svc.yearId, nutrition && nutrition.MMP && nutrition.MMP.HasNvzRiskMap),
            checkBoundaryAreas(cropping),
            checkNMaxCompliance(svc, nutrition),
            checkFarmNLoading(holding.NVZ, nutrition),
            checkPSorpCapacity(svc, cropping)
        ].filter(w => !!w)

        const localGroups = expandGroupsWithErrors(ntf);
        // call the refresh method to set the notifications
        refresh(localGroups);
        // return the local groups, so we can later use them for the 'refresh' call
        return localGroups;
    }), [svc, svc.yearId, svc.stateVersion, refresh]);

    /** Updates the 'expanded' property on each group in the given array. Returns the same array. */
    function expandGroupsWithErrors(ntf: NotificationGroup[]) {
        ntf.forEach(w => w.expanded = !!w.error);
        return ntf;
    }

    const check = () => {
        svc.recheckNotifications().then(() => refresh(local));
    }

    const navigateFunc = useCallback((location: string) => {
        if (location) {
            update(() => shell.navigate(location));
        }
    }, [update, shell?.navigate])

    const hasErrors = notifications?.some(n => n.error) ?? false;

    const cardTools =
        <div className="btn-group">
            <button className="btn btn-link btn-icon-toggle" onClick={check} title="Check again">
                <i className="fas fa-sync-alt"></i>
            </button>
        </div>

    return <Card 
        icon={`fas ${hasErrors ? 'fa-exclamation-circle' : 'fa-info-circle'}`} 
        className={hasErrors ? 'errors' : ''}
        title="Notifications" 
        small={false} 
        headerTools={cardTools}>

        <div className="row">
            <ul className="warnings">
                {notifications.map((w, index) => 
                    <li key={index}>
                        <NotificationGroupComponent group={w} navigateFunc={navigateFunc} />
                    </li>
                )}
            </ul>

        </div>
    </Card>
}